import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { COMMON_STRINGS } from '@/lib/constants/common-strings';

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /**
   * Rows-per-page choices. Supply with `onPageSizeChange` to render the control;
   * omit both to keep a fixed page size.
   */
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (size: number) => void;
}

type PageItem = number | 'ellipsis';

// ui-guidelines.md section 8: 32px controls, hairline border, 8px radius.
const CONTROL_BASE =
  'flex h-8 min-w-8 items-center justify-center rounded-lg border px-2 font-sans text-sm cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';

const CONTROL_CLASS =
  'border-border-strong bg-surface-card text-text-primary hover:bg-surface-page';

const RANGE_TEXT_CLASS = 'text-text-muted';

// `text-accent-contrast`, not `text-white`: white on the dark theme's #4DBA72
// fill is 2.45:1. The token inverts to near-black in dark (8.23:1).
const ACTIVE_PAGE_CLASS = 'border-accent bg-accent text-accent-contrast hover:bg-accent';

/**
 * Windows a 1..totalPages list down to: first, last, current, and one
 * neighbour on each side of current - with an 'ellipsis' marker for any gap.
 * Keeps a 17-page result set from rendering 17 buttons.
 */
function windowedPages(current: number, totalPages: number): PageItem[] {
  const keep = new Set<number>([1, totalPages, current]);
  if (current - 1 >= 1) keep.add(current - 1);
  if (current + 1 <= totalPages) keep.add(current + 1);

  const sorted = [...keep].sort((a, b) => a - b);
  const result: PageItem[] = [];
  let previous = 0;
  for (const pageNumber of sorted) {
    if (previous && pageNumber - previous > 1) result.push('ellipsis');
    result.push(pageNumber);
    previous = pageNumber;
  }
  return result;
}

/**
 * Range statement + windowed page controls for a server-paged list. Purely
 * presentational - the caller owns `page` and fetches the corresponding page
 * of data on `onPageChange`.
 */
export function Pagination({
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
}: PaginationProps) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const rangeText = total === 0 ? '0 of 0' : `${from}-${to} of ${total}`;
  const controlClass = `${CONTROL_BASE} ${CONTROL_CLASS}`;
  const showPageSize = Boolean(pageSizeOptions?.length && onPageSizeChange);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      {/* The range statement and the page-size control sit OUTSIDE the
          totalPages > 1 gate below: a single page still needs to say how many
          rows there are and let you ask for more per page. Gating the whole row
          on totalPages was why a 20-row result showed no pagination at all. */}
      <div className="flex items-center gap-4">
        <p className={`font-sans text-sm ${RANGE_TEXT_CLASS}`}>{rangeText}</p>
        {showPageSize && (
          <div className="flex items-center gap-2">
            <span className={`font-sans text-sm whitespace-nowrap ${RANGE_TEXT_CLASS}`}>
              {COMMON_STRINGS.TABLE.ROWS_PER_PAGE}
            </span>
            <Select
              label={COMMON_STRINGS.TABLE.ROWS_PER_PAGE}
              value={String(pageSize)}
              onChange={(event) => onPageSizeChange!(Number(event.target.value))}
              options={pageSizeOptions!.map((size) => ({
                value: String(size),
                label: String(size),
              }))}
              size="sm"
              className="w-[4.5rem]"
            />
          </div>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous page"
            className={controlClass}
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          {windowedPages(page, totalPages).map((item, index) =>
            item === 'ellipsis' ? (
              <span
                key={`ellipsis-${index}`}
                aria-hidden="true"
                className={`px-1 font-sans text-sm ${RANGE_TEXT_CLASS}`}
              >
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                aria-label={String(item)}
                aria-current={item === page ? 'page' : undefined}
                className={`${controlClass} ${item === page ? ACTIVE_PAGE_CLASS : ''}`.trim()}
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            ),
          )}
          <button
            type="button"
            aria-label="Next page"
            className={controlClass}
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
